import { Button, Mentions, Tabs } from "@arco-design/web-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import {
  ITINERARY_RESOURCE_SCENE_ORDER,
  isSafeItineraryResourceCandidateName,
  isItineraryResourceQuickExampleFallback,
  itineraryResourceSceneMeta,
  quickExamplesForItineraryResource,
} from "./config";
import { ItineraryResourceEntryUserFacingError } from "./errors";
import { resolveItineraryResourceEntries } from "./resolver";
import type {
  ItineraryResourceEntryPanelProps,
  ItineraryResourceEntryReason,
  ItineraryResourceType,
  ItineraryTransportPoint,
  ResolveItineraryResourceEntriesResult,
} from "./types";
import "./itineraryResourceEntry.css";

const { TabPane } = Tabs;

const EMPTY_RESOURCE_DRAFTS: Readonly<Record<ItineraryResourceType, string>> = Object.freeze({
  住宿: "",
  大交通: "",
  包车: "",
  考察点: "",
  餐: "",
  导游: "",
  杂费: "",
});

const TECHNICAL_MESSAGE_PATTERN = /price_required|unitPrice|错误代码|后端错误码|HTTP\s*状态|Trace\s*ID|\b(?:code|field|status|traceId|causeCode)\b|\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/iu;
const TECHNICAL_TOKEN_PATTERN = /price_required|unitPrice|\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/giu;
const TECHNICAL_PARENTHETICAL_PATTERN = /[（(][^）)]*(?:price_required|unitPrice|\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b)[^）)]*[）)]/giu;

/** 剥离混合消息中的技术标识；没有可执行中文时降级为稳定业务提示。 */
function businessErrorMessage(value: unknown, fallback: string): string {
  if (typeof value !== "string" || !/\p{Script=Han}/u.test(value)) return fallback;
  const sanitized = value
    .replace(TECHNICAL_PARENTHETICAL_PATTERN, "")
    .replace(TECHNICAL_TOKEN_PATTERN, "")
    .replace(/\s*([，。；、])\s*/gu, "$1")
    .replace(/^[，。；、\s]+|[，。；、\s]+$/gu, "")
    .replace(/，{2,}/gu, "，")
    .trim();
  return sanitized
    && /\p{Script=Han}/u.test(sanitized)
    && !TECHNICAL_MESSAGE_PATTERN.test(sanitized)
    ? sanitized
    : fallback;
}

/** 只保留可辅助修正输入、且不会暴露内部协议名称的业务明细。 */
function businessDetailText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized && !TECHNICAL_MESSAGE_PATTERN.test(normalized) ? normalized : null;
}

/** 判断杂费文本是否仍包含快捷示例中的待替换金额。 */
function containsPendingPricePlaceholder(resourceType: string, text: string): boolean {
  return resourceType === "杂费" && /[xXｘＸ]\s*元/u.test(text);
}

/** 金额仅用于共享预览展示，不参与任一功能域持久化。 */
function formatMoney(value: string | number): string {
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return String(value);
  const fraction = matched[3] ?? "";
  let cents = BigInt(matched[2]) * 100n + BigInt(`${fraction}00`.slice(0, 2));
  if ((fraction[2] ?? "0") >= "5") cents += 1n;
  const integer = String(cents / 100n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const decimal = String(cents % 100n).padStart(2, "0").replace(/0+$/, "");
  const sign = matched[1] === "-" && cents !== 0n ? "-" : "";
  return `${sign}${integer}${decimal ? `.${decimal}` : ""}`;
}

/** 公式中的数量和单价不加千分位，便于直接对照用户原始输入。 */
function formatFormulaNumber(value: string | number): string {
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return String(value);
  const integer = matched[2].replace(/^0+(?=\d)/, "");
  const fraction = (matched[3] ?? "").replace(/0+$/, "");
  const isZero = integer === "0" && fraction === "";
  return `${matched[1] === "-" && !isZero ? "-" : ""}${integer}${fraction ? `.${fraction}` : ""}`;
}

/** 按共享解析字段生成可读计算式，保持人数联动与固定数量两类口径清晰。 */
function previewFormula(entry: {
  resourceType: string;
  resourceEntryType?: string;
  spec: string;
  manualPeopleCount?: number | null;
  quantity: number;
  unit: string;
  unitPrice: number;
  unitPriceText?: string;
  nightCount: number | null;
}): string {
  const unitPrice = entry.unitPriceText ?? entry.unitPrice;
  const times = Number(entry.spec.match(/^(\d+)次$/)?.[1]);
  if (entry.resourceEntryType === "手续费" && times > 0) {
    const people = entry.manualPeopleCount && entry.manualPeopleCount > 0
      ? entry.manualPeopleCount
      : entry.quantity / times;
    if (people > 0) {
      return `${formatFormulaNumber(people)}人 x ${times}次 x ${formatFormulaNumber(unitPrice)}元`;
    }
  }
  if (entry.unit === "人晚" && entry.nightCount && entry.nightCount > 0) {
    return `${formatFormulaNumber(entry.quantity / entry.nightCount)}人 x ${formatFormulaNumber(entry.nightCount)}晚 x ${formatFormulaNumber(unitPrice)}元`;
  }
  const manualPeople = Number(entry.manualPeopleCount);
  if (
    (entry.resourceType === "考察点" || entry.resourceType === "餐")
    && manualPeople > 0
    && entry.quantity > 0
  ) {
    const billingFactor = entry.quantity / manualPeople;
    const billingUnit = entry.resourceType === "餐" ? "餐" : "次";
    return `${formatFormulaNumber(manualPeople)}人 x ${formatFormulaNumber(billingFactor)}${billingUnit} x ${formatFormulaNumber(unitPrice)}元`;
  }
  return `${formatFormulaNumber(entry.quantity)}${entry.unit} x ${formatFormulaNumber(unitPrice)}元`;
}

/** 把调用端或未知异常规整为共享面板可展示的结构化错误。 */
function normalizePanelReason(
  error: unknown,
  fallback: Pick<ItineraryResourceEntryReason, "code" | "field" | "message">,
): ItineraryResourceEntryReason {
  if (typeof error !== "object" || error === null) return fallback;
  const candidate = error as Partial<ItineraryResourceEntryReason>;
  return {
    code: typeof candidate.code === "string" ? candidate.code : fallback.code,
    field: typeof candidate.field === "string" ? candidate.field : fallback.field,
    message: businessErrorMessage(candidate.message, fallback.message),
    ...(typeof candidate.value === "string" ? { value: candidate.value } : {}),
    ...(typeof candidate.lineNumber === "number" ? { lineNumber: candidate.lineNumber } : {}),
    ...(Array.isArray(candidate.candidates) ? { candidates: candidate.candidates } : {}),
    ...(typeof candidate.causeCode === "string" ? { causeCode: candidate.causeCode } : {}),
    ...(typeof candidate.status === "number" ? { status: candidate.status } : {}),
    ...(typeof candidate.traceId === "string" ? { traceId: candidate.traceId } : {}),
  };
}

/** 端口异常默认不可信；只有本进程专用错误类型可携带可执行业务明细。 */
function normalizePortReason(
  error: unknown,
  fallback: Pick<ItineraryResourceEntryReason, "code" | "field" | "message">,
): ItineraryResourceEntryReason {
  return error instanceof ItineraryResourceEntryUserFacingError
    ? normalizePanelReason(error.reason, fallback)
    : fallback;
}

/** 解析器失败结果进入组件状态前统一转成业务安全消息，错误区和预览共用。 */
function normalizePanelResult(
  result: ResolveItineraryResourceEntriesResult,
): ResolveItineraryResourceEntriesResult {
  if (result.ok) return result;
  const reason = normalizePanelReason(result.reason, {
    code: "entry_resolution_failed",
    field: "text",
    message: result.reason.code === "transport_point_search_failed"
      ? "大交通点位查询失败，请稍后重试"
      : "当前录入无法识别，请检查后重试",
  });
  if (result.reason.code === "transport_point_search_failed") {
    reason.message = "大交通点位查询失败，请稍后重试";
    delete reason.candidates;
  }
  return { ok: false, error: reason.message, reason };
}

/** 结构化录入错误，供实时预览、候选查询与加入失败共用。 */
function EntryReasonDetails({ reason }: { reason: ItineraryResourceEntryReason }) {
  const value = businessDetailText(reason.value);
  const candidates = reason.candidates
    ?.map(businessDetailText)
    .filter((item): item is string => Boolean(item)) ?? [];
  return (
    <div className="itinerary-resource-entry-panel__error-detail">
      <p>{reason.message}</p>
      {value || reason.lineNumber || candidates.length ? <dl>
        {value ? <><dt>输入值</dt><dd>{value}</dd></> : null}
        {reason.lineNumber ? <><dt>行号</dt><dd>{reason.lineNumber}</dd></> : null}
        {candidates.length ? <><dt>候选</dt><dd>{candidates.join("、")}</dd></> : null}
      </dl> : null}
    </div>
  );
}

/** 从当前输入行判断大交通候选查询模式。 */
function transportModeForText(text: string): "air" | "rail" | null {
  const firstWord = text.split(/\r?\n/).at(-1)?.trim().split(/\s+/)[0];
  if (firstWord === "飞机") return "air";
  return /^(火车|高铁)$/.test(firstWord ?? "") ? "rail" : null;
}

/** 统一展示交通点名称、城市与点位类型。 */
function transportOptionLabel(item: ItineraryTransportPoint): string {
  const suffix = [item.city, item.pointType].filter(Boolean).join("/");
  return suffix ? `${item.name} · ${suffix}` : item.name;
}

/**
 * 行程资源共享快速录入面板：统一七类场景、候选、解析、预览与原子加入交互。
 */
export function ItineraryResourceEntryPanel({
  context,
  disabled,
  commitDisabled = false,
  initialResourceType = "住宿",
  resourceType,
  showResourceTabs = true,
  subjectSlot,
  searchTransportPoints,
  onAdd,
  onAddingChange,
  onDraftStateChange,
  onDraftTextsChange,
  onResourceTypeChange,
}: ItineraryResourceEntryPanelProps) {
  const fallbackNoteId = useId();
  const previewLabelId = useId();
  const [internalResourceType, setInternalResourceType] = useState(initialResourceType);
  const activeResourceType = resourceType ?? internalResourceType;
  const [resourceDrafts, setResourceDrafts] = useState<Record<ItineraryResourceType, string>>(
    () => ({ ...EMPTY_RESOURCE_DRAFTS }),
  );
  const text = resourceDrafts[activeResourceType];
  const [previewResult, setPreviewResult] = useState<ResolveItineraryResourceEntriesResult | null>(null);
  const [explicitReason, setExplicitReason] = useState<ItineraryResourceEntryReason | null>(null);
  const [resolving, setResolving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [transportOptions, setTransportOptions] = useState<ItineraryTransportPoint[]>([]);
  const previewGenerationRef = useRef(0);
  const addRequestGenerationRef = useRef(0);
  const transportQueryGenerationRef = useRef(0);
  const transportMentionSearchKeyRef = useRef<string | null>(null);
  const activeResourceTypeRef = useRef(activeResourceType);
  const textRef = useRef(text);
  const contextRef = useRef(context);
  const searchTransportPointsRef = useRef(searchTransportPoints);
  const writeBlockedRef = useRef(disabled || commitDisabled);
  activeResourceTypeRef.current = activeResourceType;
  textRef.current = text;
  contextRef.current = context;
  searchTransportPointsRef.current = searchTransportPoints;
  writeBlockedRef.current = disabled || commitDisabled;
  const mentionsRef = useRef<HTMLDivElement | null>(null);
  const mentionKeyupFallbackRef = useRef<number | null>(null);
  const mentionFallbackCaretRef = useRef(0);
  const mentionOptionSelectingRef = useRef(false);
  const mentionOptionSelectionResetRef = useRef<number | null>(null);
  const mentionPhysicalInputRef = useRef(false);

  const sceneMeta = itineraryResourceSceneMeta(activeResourceType);
  const pendingPricePlaceholder = containsPendingPricePlaceholder(activeResourceType, text);
  const hiddenDraftTypes = ITINERARY_RESOURCE_SCENE_ORDER.filter(
    (type) => type !== activeResourceType && resourceDrafts[type].trim().length > 0,
  );

  /** 只更新当前资源类型的草稿，其他六类输入保持不变。 */
  function setText(next: string | ((current: string) => string)) {
    const targetResourceType = activeResourceTypeRef.current;
    setResourceDrafts((current) => {
      const nextText = typeof next === "function"
        ? next(current[targetResourceType])
        : next;
      return nextText === current[targetResourceType]
        ? current
        : { ...current, [targetResourceType]: nextText };
    });
  }

  useEffect(() => {
    onAddingChange?.(adding);
  }, [adding, onAddingChange]);
  useEffect(() => {
    onDraftStateChange?.(Object.values(resourceDrafts).some((draft) => draft.trim().length > 0));
  }, [onDraftStateChange, resourceDrafts]);
  useEffect(() => { onDraftTextsChange?.({ ...resourceDrafts }); }, [onDraftTextsChange, resourceDrafts]);
  const quickExamples = useMemo(
    () => quickExamplesForItineraryResource(
      activeResourceType,
      context.entryOptions,
      context.itineraryDayCount,
    ),
    [activeResourceType, context.entryOptions, context.itineraryDayCount],
  );
  const hasFallbackQuickExample = quickExamples.some(
    (example) => isItineraryResourceQuickExampleFallback(activeResourceType, example),
  );
  const requiresItineraryCandidate = new Set(["住宿", "考察点", "餐"])
    .has(activeResourceType);
  const entryPlaceholder = quickExamples[0]
    ? `例如：${quickExamples[0]}`
    : activeResourceType === "考察点"
      ? context.entryOptions.points.length === 0
        ? "当前行程暂无考察点，请先完善行程信息"
        : "例如：@考察点 20人 100元"
      : requiresItineraryCandidate
        ? "当前行程暂无可用录入项，请先完善行程信息"
        : `例如：${activeResourceType}项目 1项 100`;

  useEffect(() => {
    /** 实体键盘已把光标推进到预期位置时取消兜底测量。 */
    function cancelMentionKeyupFallback(event: KeyboardEvent) {
      const target = event.target;
      if (!(target instanceof Node) || !mentionsRef.current?.contains(target)) return;
      mentionPhysicalInputRef.current = false;
      const textarea = mentionsRef.current.querySelector("textarea");
      if (
        textarea?.selectionStart !== mentionFallbackCaretRef.current
        || textarea.selectionEnd !== mentionFallbackCaretRef.current
      ) return;
      if (mentionKeyupFallbackRef.current !== null) {
        window.clearTimeout(mentionKeyupFallbackRef.current);
        mentionKeyupFallbackRef.current = null;
      }
    }

    /** 标记鼠标或回车选择候选，避免回填后重新打开弹层。 */
    function markMentionOptionSelection(event: Event) {
      const target = event.target;
      const isMentionKeydown = event instanceof KeyboardEvent
        && event.type === "keydown"
        && target instanceof Node
        && Boolean(mentionsRef.current?.contains(target));
      const selectsByClick = event.type === "click"
        && target instanceof Element
        && Boolean(target.closest('[role="option"], .arco-select-option'));
      const selectsByEnter = isMentionKeydown
        && event.key === "Enter"
        && Boolean(document.querySelector('[role="listbox"] [role="option"], .arco-select-option'));
      if (isMentionKeydown && !selectsByEnter) mentionPhysicalInputRef.current = true;
      if (!selectsByClick && !selectsByEnter) return;
      mentionOptionSelectingRef.current = true;
      if (mentionOptionSelectionResetRef.current !== null) {
        window.clearTimeout(mentionOptionSelectionResetRef.current);
      }
      mentionOptionSelectionResetRef.current = window.setTimeout(() => {
        mentionOptionSelectingRef.current = false;
        mentionOptionSelectionResetRef.current = null;
      }, 500);
    }

    document.addEventListener("keyup", cancelMentionKeyupFallback, true);
    document.addEventListener("click", markMentionOptionSelection, true);
    document.addEventListener("keydown", markMentionOptionSelection, true);
    return () => {
      previewGenerationRef.current += 1;
      addRequestGenerationRef.current += 1;
      transportQueryGenerationRef.current += 1;
      document.removeEventListener("keyup", cancelMentionKeyupFallback, true);
      document.removeEventListener("click", markMentionOptionSelection, true);
      document.removeEventListener("keydown", markMentionOptionSelection, true);
      if (mentionKeyupFallbackRef.current !== null) window.clearTimeout(mentionKeyupFallbackRef.current);
      if (mentionOptionSelectionResetRef.current !== null) {
        window.clearTimeout(mentionOptionSelectionResetRef.current);
      }
    };
  }, []);

  useEffect(() => {
    previewGenerationRef.current += 1;
    addRequestGenerationRef.current += 1;
    transportQueryGenerationRef.current += 1;
    transportMentionSearchKeyRef.current = null;
    setAdding(false);
    setTransportOptions([]);
    setExplicitReason(null);
  }, [activeResourceType, context, searchTransportPoints]);

  useEffect(() => {
    const normalized = text.trim();
    const generation = ++previewGenerationRef.current;
    setExplicitReason(null);
    if (!normalized) {
      setPreviewResult(null);
      setResolving(false);
      return;
    }
    setResolving(true);
    void resolveItineraryResourceEntries({
      text: normalized,
      activeResourceType,
      context,
      searchTransportPoints,
      validateTransport: false,
    }).then((result) => {
      if (generation !== previewGenerationRef.current) return;
      setPreviewResult(normalizePanelResult(result));
      setResolving(false);
    });
  }, [activeResourceType, context, searchTransportPoints, text]);

  useEffect(() => {
    addRequestGenerationRef.current += 1;
    setAdding(false);
  }, [activeResourceType, context]);

  useEffect(() => {
    if (!disabled && !commitDisabled) return;
    addRequestGenerationRef.current += 1;
    setAdding(false);
  }, [commitDisabled, disabled]);

  /** 切换当前资源类型；受控模式只通知调用端，非受控模式同步内部状态。 */
  function changeResourceType(next: string) {
    const typed = next as ItineraryResourceType;
    if (disabled || adding) return;
    addRequestGenerationRef.current += 1;
    previewGenerationRef.current += 1;
    setAdding(false);
    setResolving(false);
    setPreviewResult(null);
    setExplicitReason(null);
    if (resourceType === undefined) setInternalResourceType(typed);
    transportQueryGenerationRef.current += 1;
    setTransportOptions([]);
    transportMentionSearchKeyRef.current = null;
    onResourceTypeChange?.(typed);
  }

  const mentionStayAreas = context.entryOptions.stayAreas.filter((item) => (
    isSafeItineraryResourceCandidateName(item.name)
    && (activeResourceType !== "住宿" || (Number.isFinite(item.nightCount) && item.nightCount > 0))
  ));
  const mentionOptions = activeResourceType === "住宿" || activeResourceType === "餐"
    ? mentionStayAreas.map((item) => ({ label: item.name, value: item.name }))
    : activeResourceType === "考察点"
      ? context.entryOptions.points
          .filter((item) => isSafeItineraryResourceCandidateName(item.name))
          .map((item) => ({ label: item.name, value: item.name }))
      : activeResourceType === "大交通"
        ? transportOptions
            .filter((item) => isSafeItineraryResourceCandidateName(item.name))
            .map((item) => ({
              value: item.name,
              label: transportOptionLabel(item),
            }))
        : [];

  /** Mentions 搜索：本地场景交给 Arco 过滤，大交通按当前行交通方式异步查询。 */
  async function handleMentionSearch(keyword: string) {
    if (activeResourceType !== "大交通") return;
    const mode = transportModeForText(text);
    if (!mode) {
      transportQueryGenerationRef.current += 1;
      transportMentionSearchKeyRef.current = null;
      setTransportOptions([]);
      return;
    }
    const searchKey = `${mode}:${keyword}`;
    if (transportMentionSearchKeyRef.current === searchKey) return;
    transportMentionSearchKeyRef.current = searchKey;
    const queryGeneration = ++transportQueryGenerationRef.current;
    const queryContext = context;
    const queryPort = searchTransportPoints;
    const isCurrentQuery = () => (
      queryGeneration === transportQueryGenerationRef.current
      && activeResourceTypeRef.current === "大交通"
      && transportModeForText(textRef.current) === mode
      && transportMentionSearchKeyRef.current === searchKey
      && contextRef.current === queryContext
      && searchTransportPointsRef.current === queryPort
    );
    setExplicitReason(null);
    try {
      const points = await searchTransportPoints(mode, keyword);
      if (isCurrentQuery()) setTransportOptions(points);
    } catch (error) {
      if (!isCurrentQuery()) return;
      setTransportOptions([]);
      setExplicitReason(normalizePortReason(error, {
        code: "transport_point_search_failed",
        field: "transportPoint",
        message: "大交通点位查询失败",
      }));
    }
  }

  /** 无候选弹层时阻止 Mentions 拦截 Enter，保留 textarea 原生换行。 */
  function handleEntryKeyDownCapture(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== "Enter") return;
    if (!document.querySelector(".arco-select-popup")) event.stopPropagation();
  }

  /** 无真实键盘输入时补触发 Mentions 候选测量，覆盖粘贴与输入法提交。 */
  function scheduleMentionKeyupFallback() {
    if (mentionKeyupFallbackRef.current !== null) window.clearTimeout(mentionKeyupFallbackRef.current);
    mentionKeyupFallbackRef.current = window.setTimeout(() => {
      mentionKeyupFallbackRef.current = null;
      const textarea = mentionsRef.current?.querySelector("textarea");
      if (textarea && document.activeElement === textarea) {
        textarea.setSelectionRange(mentionFallbackCaretRef.current, mentionFallbackCaretRef.current);
        textarea.dispatchEvent(new KeyboardEvent("keyup", { key: "Unidentified", bubbles: true }));
      }
    });
  }

  /** 兼容住宿候选展开、粘贴和输入法提交，并避免物理键盘重复触发候选查询。 */
  function handleTextChange(value: string) {
    addRequestGenerationRef.current += 1;
    setAdding(false);
    textRef.current = value;
    transportQueryGenerationRef.current += 1;
    transportMentionSearchKeyRef.current = null;
    setTransportOptions([]);
    if (activeResourceType === "住宿") {
      const selectedStay = context.entryOptions.stayAreas.find((item) => {
        const mention = `@${item.name}`;
        const selectedByMention = mentionOptionSelectingRef.current && value.endsWith(mention);
        const selectedWithSplit = value.endsWith(`${mention} `);
        return (selectedByMention || selectedWithSplit) && !text.endsWith(value.slice(-mention.length));
      });
      if (selectedStay && Number.isFinite(selectedStay.nightCount) && selectedStay.nightCount > 0) {
        const mention = `@${selectedStay.name}`;
        const mentionSuffix = value.endsWith(`${mention} `) ? `${mention} ` : mention;
        const prefix = value.slice(0, -mentionSuffix.length);
        const parsedPrice = Number(selectedStay.recommendedPrice || 0);
        const price = Number.isFinite(parsedPrice) && parsedPrice >= 0 ? parsedPrice : 0;
        setText(`${prefix}${selectedStay.name} 学生 商务连锁 ${selectedStay.nightCount}晚 ${price}`);
        mentionOptionSelectingRef.current = false;
        return;
      }
    }
    let commonPrefixLength = 0;
    while (
      commonPrefixLength < text.length
      && commonPrefixLength < value.length
      && text[commonPrefixLength] === value[commonPrefixLength]
    ) commonPrefixLength += 1;
    let commonSuffixLength = 0;
    while (
      commonSuffixLength < text.length - commonPrefixLength
      && commonSuffixLength < value.length - commonPrefixLength
      && text[text.length - commonSuffixLength - 1] === value[value.length - commonSuffixLength - 1]
    ) commonSuffixLength += 1;
    mentionFallbackCaretRef.current = value.length - commonSuffixLength;
    setText(value);
    if (mentionKeyupFallbackRef.current !== null) window.clearTimeout(mentionKeyupFallbackRef.current);
    if (mentionOptionSelectingRef.current) {
      mentionOptionSelectingRef.current = false;
      if (mentionOptionSelectionResetRef.current !== null) {
        window.clearTimeout(mentionOptionSelectionResetRef.current);
        mentionOptionSelectionResetRef.current = null;
      }
      return;
    }
    if (!mentionPhysicalInputRef.current) scheduleMentionKeyupFallback();
  }

  /** 快捷示例按行追加，保留用户已输入的批量文本。 */
  function appendQuickExample(example: string) {
    if (disabled || commitDisabled || adding) return;
    addRequestGenerationRef.current += 1;
    setAdding(false);
    setText((current) => {
      const normalized = current.replace(/[ \t]+$/g, "");
      if (!normalized) return example;
      return normalized.endsWith("\n") ? `${normalized}${example}` : `${normalized}\n${example}`;
    });
    mentionsRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
  }

  /** 重新解析当前全文，整批成功后只调用一次 onAdd；任何失败均保留原文本。 */
  async function handleAdd() {
    if (
      writeBlockedRef.current
      || adding
      || resolving
      || previewResult?.ok !== true
      || !text.trim()
      || pendingPricePlaceholder
    ) return;
    const generation = ++addRequestGenerationRef.current;
    const resourceTypeAtStart = activeResourceType;
    const contextAtStart = context;
    const isCurrentRequest = () => (
      generation === addRequestGenerationRef.current
      && activeResourceTypeRef.current === resourceTypeAtStart
      && contextRef.current === contextAtStart
      && !writeBlockedRef.current
    );
    setAdding(true);
    setExplicitReason(null);
    try {
      const result = normalizePanelResult(await resolveItineraryResourceEntries({
        text: text.trim(),
        activeResourceType,
        context,
        searchTransportPoints,
      }));
      if (!isCurrentRequest()) return;
      setPreviewResult(result);
      if (!result.ok) return;
      if (!isCurrentRequest()) return;
      try {
        await onAdd(result.entries);
      } catch (error) {
        if (isCurrentRequest()) {
          setExplicitReason(normalizePortReason(error, {
            code: "entry_add_failed",
            field: "entries",
            message: "加入明细失败，请稍后重试",
          }));
        }
        return;
      }
      if (isCurrentRequest()) {
        setText("");
        setPreviewResult(null);
      }
    } finally {
      if (generation === addRequestGenerationRef.current) setAdding(false);
    }
  }

  const displayReason = pendingPricePlaceholder
    ? {
        code: "pending_price_placeholder",
        field: "unitPrice",
        message: "请将 x 替换为实际金额后再加入明细",
      }
    : explicitReason ?? (previewResult && !previewResult.ok ? previewResult.reason : null);

  return (
    <section className="itinerary-resource-entry-panel" aria-label="行程资源快速录入">
      {showResourceTabs ? (
        <div role="tablist" aria-label="资源类型">
          <Tabs
            activeTab={activeResourceType}
            onChange={changeResourceType}
            className="itinerary-resource-entry-panel__tabs"
          >
            {ITINERARY_RESOURCE_SCENE_ORDER.map((type) => (
              <TabPane key={type} title={type} disabled={disabled || adding} />
            ))}
          </Tabs>
        </div>
      ) : null}
      <div className={`itinerary-resource-entry-panel__layout${subjectSlot ? " itinerary-resource-entry-panel__layout--with-subject" : ""}`}>
        <div className="itinerary-resource-entry-panel__title">
          <span>{activeResourceType}录入</span>
          <small data-testid="entry-help">{sceneMeta.help} 计算：{sceneMeta.formula}</small>
        </div>
        {hiddenDraftTypes.length > 0 ? (
          <div className="itinerary-resource-entry-panel__pending-drafts" role="note">
            <span>其他分类有未加入内容：{hiddenDraftTypes.join("、")}</span>{hiddenDraftTypes.map((type) => (
              <Button key={type} size="mini" disabled={disabled || adding} onClick={() => changeResourceType(type)}>返回{type}录入</Button>
            ))}
          </div>
        ) : null}
        {subjectSlot ? (
          <div className="itinerary-resource-entry-panel__subject">{subjectSlot}</div>
        ) : null}
        <div className="itinerary-resource-entry-panel__main">
          <Mentions
            ref={mentionsRef}
            aria-label="自然语言录入"
            className="itinerary-resource-entry-panel__input"
            rows={4}
            prefix="@"
            split={activeResourceType === "大交通" ? "" : " "}
            alignTextarea={false}
            options={mentionOptions}
            filterOption={activeResourceType === "大交通" ? false : undefined}
            onSearch={(keyword) => void handleMentionSearch(keyword)}
            placeholder={entryPlaceholder}
            value={text}
            disabled={disabled}
            onChange={handleTextChange}
            onKeyDownCapture={handleEntryKeyDownCapture}
            onCompositionStart={() => { mentionPhysicalInputRef.current = true; }}
            onCompositionEnd={() => {
              mentionPhysicalInputRef.current = false;
              scheduleMentionKeyupFallback();
            }}
          />
          {displayReason ? <div role="alert"><EntryReasonDetails reason={displayReason} /></div> : null}
        </div>
        <aside className="itinerary-resource-entry-panel__preview" aria-labelledby={previewLabelId}>
          <div id={previewLabelId} className="itinerary-resource-entry-panel__preview-label">实时预览</div>
          <div
            data-testid="itinerary-resource-entry-preview"
            className="itinerary-resource-entry-panel__preview-box"
            role={previewResult && !previewResult.ok ? undefined : "status"}
          >
            {!text.trim() ? (
              "选择大类并输入一句话，或点击示例快速填入。"
            ) : resolving ? (
              "正在识别…"
            ) : previewResult?.ok ? (
              previewResult.entries.map((entry, index) => (
                <div
                  data-testid="itinerary-resource-entry-preview-item"
                  className="itinerary-resource-entry-panel__preview-item"
                  key={`${entry.lineNumber ?? "line"}-${index}-${entry.itemName}-${entry.travelerRole ?? "all"}`}
                >
                  <strong>
                    {entry.itemName && entry.itemName !== entry.subtype
                      ? `${entry.subtype} / ${entry.itemName}`
                      : entry.subtype}
                  </strong>
                  <div className="itinerary-resource-entry-panel__preview-formula">
                    {entry.travelerRole ? `${entry.travelerRole}；` : ""}{previewFormula(entry)}
                  </div>
                  <div>规格：{entry.spec || "—"}</div>
                  <div>单价：{formatMoney(entry.unitPriceText ?? entry.unitPrice)}；预计金额：<strong>¥{formatMoney(entry.amount)}</strong></div>
                </div>
              ))
            ) : (
              previewResult && !previewResult.ok
                ? "未生成预览，请根据上方提示修改输入。"
                : "等待识别"
            )}
          </div>
        </aside>
        <div className="itinerary-resource-entry-panel__examples">
          {hasFallbackQuickExample ? (
            <span
              id={fallbackNoteId}
              className="itinerary-resource-entry-panel__examples-note"
              role="note"
            >
              格式示例（需替换占位内容）
            </span>
          ) : null}
          <div className="itinerary-resource-entry-panel__chips">
            {quickExamples.map((example) => {
              const isFallback = isItineraryResourceQuickExampleFallback(activeResourceType, example);
              return (
                <button
                  key={example}
                  type="button"
                  className="itinerary-resource-entry-panel__quick-chip"
                  aria-describedby={isFallback ? fallbackNoteId : undefined}
                  title={isFallback ? "格式示例：请替换为当前行程的真实候选后再加入明细" : undefined}
                  disabled={disabled || commitDisabled || adding}
                  onClick={() => appendQuickExample(example)}
                >
                  {example}
                </button>
              );
            })}
          </div>
          <Button
            type="primary"
            loading={adding}
            aria-busy={adding}
            disabled={
              disabled
              || commitDisabled
              || adding
              || resolving
              || previewResult?.ok !== true
              || pendingPricePlaceholder
              || !text.trim()
            }
            onClick={() => void handleAdd()}
          >
            加入明细
          </Button>
          <Button disabled={disabled || adding || !text.trim()} onClick={() => {
            setText("");
            setExplicitReason(null);
            setPreviewResult(null);
            mentionsRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
          }}>清空当前录入</Button>
        </div>
      </div>
    </section>
  );
}
